"use client";

import { c as _c } from "react/compiler-runtime";
import type { FormEvent } from "react";
import { useContext, useEffect, useState } from "react";
import type { ApiProfileRaterCicState } from "@/entities/IProfile";
import { getStringAsNumberOrZero } from "@/helpers/Helpers";
import { AuthContext } from "@/components/auth/Auth";
import { commonApiFetch, commonApiPost } from "@/services/api/common-api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { QueryKey, ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { createBreakpoint } from "react-use";
import UserRateAdjustmentHelper from "@/components/user/utils/rate/UserRateAdjustmentHelper";
import CircleLoader from "@/components/distribution-plan-tool/common/CircleLoader";
import { ApiProfileProxyActionType } from "@/generated/models/ApiProfileProxyActionType";
import UserPageIdentityHeaderCICRateStats from "./UserPageIdentityHeaderCICRateStats";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
const useBreakpoint = createBreakpoint({
  MD: 768,
  S: 0
});
export default function UserPageIdentityHeaderCICRate(t0) {
  const $ = _c(148);
  const {
    profile,
    isTooltip
  } = t0;
  const {
    address
  } = useSeizeConnectContext();
  const {
    requestAuth,
    setToast,
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    onProfileCICModify
  } = useContext(ReactQueryWrapperContext);
  let t1;
  if ($[0] !== profile.handle) {
    t1 = profile?.handle?.toLowerCase();
    $[0] = profile.handle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activeProfileProxy?.created_by.handle || $[3] !== address) {
    t2 = activeProfileProxy?.created_by.handle ?? address?.toLowerCase();
    $[2] = activeProfileProxy?.created_by.handle;
    $[3] = address;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = [QueryKey.PROFILE_RATER_CIC_STATE, {
      handle: t1,
      rater: t2
    }];
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== activeProfileProxy?.created_by || $[9] !== address || $[10] !== profile.query) {
    t4 = async () => await commonApiFetch({
      endpoint: `profiles/${profile.query}/cic/rating/${activeProfileProxy?.created_by.handle ?? address?.toLowerCase()}`
    });
    $[8] = activeProfileProxy?.created_by;
    $[9] = address;
    $[10] = profile.query;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = !!address;
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      staleTime: 0
    };
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const {
    data: currentCICState
  } = useQuery(t6);
  const [mutating, setMutating] = useState(false);
  let t7;
  if ($[16] !== profile.query) {
    t7 = async amount => {
      setMutating(true);
      return await commonApiPost({
        endpoint: `profiles/${profile.query}/cic/rating`,
        body: {
          amount
        }
      });
    };
    $[16] = profile.query;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== activeProfileProxy || $[19] !== address || $[20] !== connectedProfile || $[21] !== onProfileCICModify || $[22] !== profile || $[23] !== setToast) {
    t8 = () => {
      setToast({
        message: "NIC rating updated.",
        type: "success"
      });
      onProfileCICModify({
        targetProfile: profile,
        connectedProfile: connectedProfile ?? null,
        rater: activeProfileProxy?.created_by.handle ?? address?.toLowerCase() ?? null,
        profileProxy: activeProfileProxy ?? null
      });
    };
    $[18] = activeProfileProxy;
    $[19] = address;
    $[20] = connectedProfile;
    $[21] = onProfileCICModify;
    $[22] = profile;
    $[23] = setToast;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== setToast) {
    t9 = error => {
      setToast({
        message: error as unknown as string,
        type: "error"
      });
    };
    $[25] = setToast;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setMutating(false);
    };
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t11 = {
      mutationFn: t7,
      onSuccess: t8,
      onError: t9,
      onSettled: t10
    };
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const updateCICMutation = useMutation(t11);
  let t12;
  if ($[32] !== activeProfileProxy?.actions) {
    t12 = () => {
      const repProxy = activeProfileProxy?.actions.find(_temp);
      if (!repProxy) {
        return null;
      }
      return Math.max((repProxy.credit_amount ?? 0) - (repProxy.credit_spent ?? 0), 0);
    };
    $[32] = activeProfileProxy?.actions;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const getProxyAvailableCredit = t12;
  const [proxyAvailableCredit, setProxyAvailableCredit] = useState(getProxyAvailableCredit());
  let t13;
  if ($[34] !== getProxyAvailableCredit) {
    t13 = () => setProxyAvailableCredit(getProxyAvailableCredit());
    $[34] = getProxyAvailableCredit;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== activeProfileProxy) {
    t14 = [activeProfileProxy];
    $[36] = activeProfileProxy;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  useEffect(t13, t14);
  let t15;
  if ($[38] !== currentCICState?.cic_rating_by_rater || $[39] !== currentCICState?.cic_ratings_left_to_give_by_rater || $[40] !== proxyAvailableCredit) {
    t15 = () => {
      const currentCic = currentCICState?.cic_rating_by_rater ?? 0;
      const heroAvailableCic = currentCICState?.cic_ratings_left_to_give_by_rater ?? 0;
      const minHeroCic = 0 - (Math.abs(currentCic) + heroAvailableCic);
      if (typeof proxyAvailableCredit !== "number") {
        return minHeroCic;
      }
      const minProxyRep = currentCic - proxyAvailableCredit;
      return Math.abs(minHeroCic) < Math.abs(minProxyRep) ? minHeroCic : minProxyRep;
    };
    $[38] = currentCICState?.cic_rating_by_rater;
    $[39] = currentCICState?.cic_ratings_left_to_give_by_rater;
    $[40] = proxyAvailableCredit;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const getMinValue = t15;
  let t16;
  if ($[42] !== currentCICState?.cic_rating_by_rater || $[43] !== currentCICState?.cic_ratings_left_to_give_by_rater || $[44] !== proxyAvailableCredit) {
    t16 = () => {
      const currentCic_0 = currentCICState?.cic_rating_by_rater ?? 0;
      const heroAvailableCic_0 = currentCICState?.cic_ratings_left_to_give_by_rater ?? 0;
      const maxHeroCic = Math.abs(currentCic_0) + heroAvailableCic_0;
      if (typeof proxyAvailableCredit !== "number") {
        return maxHeroCic;
      }
      const maxProxyRep = currentCic_0 + proxyAvailableCredit;
      return Math.min(maxHeroCic, maxProxyRep);
    };
    $[42] = currentCICState?.cic_rating_by_rater;
    $[43] = currentCICState?.cic_ratings_left_to_give_by_rater;
    $[44] = proxyAvailableCredit;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const getMaxValue = t16;
  let t17;
  if ($[46] !== getMaxValue || $[47] !== getMinValue) {
    t17 = () => ({
      min: getMinValue(),
      max: getMaxValue()
    });
    $[46] = getMaxValue;
    $[47] = getMinValue;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  const getMinMaxValues = t17;
  let t18;
  if ($[49] !== getMinMaxValues) {
    t18 = getMinMaxValues();
    $[49] = getMinMaxValues;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  const [minMaxValues, setMinMaxValues] = useState(t18);
  let t19;
  if ($[51] !== getMinMaxValues) {
    t19 = () => setMinMaxValues(getMinMaxValues());
    $[51] = getMinMaxValues;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== currentCICState || $[54] !== proxyAvailableCredit) {
    t20 = [currentCICState, proxyAvailableCredit];
    $[53] = currentCICState;
    $[54] = proxyAvailableCredit;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  useEffect(t19, t20);
  const [originalRating, setOriginalRating] = useState(currentCICState?.cic_rating_by_rater ?? 0);
  const [adjustedRatingStr, setAdjustedRatingStr] = useState(`${originalRating}`);
  const getValueStr = _temp2;
  let t21;
  if ($[56] !== currentCICState?.cic_rating_by_rater) {
    t21 = () => {
      setOriginalRating(currentCICState?.cic_rating_by_rater ?? 0);
      setAdjustedRatingStr(`${currentCICState?.cic_rating_by_rater ?? 0}`);
    };
    $[56] = currentCICState?.cic_rating_by_rater;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== currentCICState) {
    t22 = [currentCICState];
    $[58] = currentCICState;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  useEffect(t21, t22);
  let t23;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = e => {
      const inputValue = e.currentTarget.value;
      const strCIC_0 = ["-0", "0-"].includes(inputValue) ? "-" : inputValue;
      if (/^-?\d*$/.test(strCIC_0)) {
        const newCicValue = getValueStr(strCIC_0);
        setAdjustedRatingStr(newCicValue);
      }
    };
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  const onValueChange = t23;
  let t24;
  if ($[61] !== activeProfileProxy || $[62] !== adjustedRatingStr || $[63] !== getMinMaxValues) {
    t24 = () => {
      if (activeProfileProxy) {
        return;
      }
      const {
        min,
        max
      } = getMinMaxValues();
      const valueAsNumber = getStringAsNumberOrZero(adjustedRatingStr);
      if (valueAsNumber > max) {
        setAdjustedRatingStr(`${max}`);
        return;
      }
      if (valueAsNumber < min) {
        setAdjustedRatingStr(`${min}`);
      }
    };
    $[61] = activeProfileProxy;
    $[62] = adjustedRatingStr;
    $[63] = getMinMaxValues;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  const adjustStrValueToMinMax = t24;
  let t25;
  if ($[65] !== activeProfileProxy || $[66] !== adjustedRatingStr || $[67] !== minMaxValues) {
    t25 = () => {
      if (activeProfileProxy) {
        return true;
      }
      const {
        min: min_0,
        max: max_0
      } = minMaxValues;
      const valueAsNumber_0 = getStringAsNumberOrZero(adjustedRatingStr);
      if (valueAsNumber_0 > max_0) {
        return false;
      }
      if (valueAsNumber_0 < min_0) {
        return false;
      }
      return true;
    };
    $[65] = activeProfileProxy;
    $[66] = adjustedRatingStr;
    $[67] = minMaxValues;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  const getIsValidValue = t25;
  const [isValidValue, setIsValidValue] = useState(getIsValidValue());
  let t26;
  if ($[69] !== getIsValidValue) {
    t26 = () => setIsValidValue(getIsValidValue());
    $[69] = getIsValidValue;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== adjustedRatingStr) {
    t27 = [adjustedRatingStr];
    $[71] = adjustedRatingStr;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  useEffect(t26, t27);
  let t28;
  if ($[73] !== adjustedRatingStr) {
    t28 = getStringAsNumberOrZero(adjustedRatingStr);
    $[73] = adjustedRatingStr;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  const [newRating, setNewRating] = useState(t28);
  let t29;
  let t30;
  if ($[75] !== adjustedRatingStr) {
    t29 = () => {
      setNewRating(getStringAsNumberOrZero(adjustedRatingStr));
    };
    t30 = [adjustedRatingStr];
    $[75] = adjustedRatingStr;
    $[76] = t29;
    $[77] = t30;
  } else {
    t29 = $[76];
    t30 = $[77];
  }
  useEffect(t29, t30);
  const [haveChanged, setHaveChanged] = useState(newRating !== originalRating);
  let t31;
  let t32;
  if ($[78] !== newRating || $[79] !== originalRating) {
    t31 = () => {
      setHaveChanged(newRating !== originalRating);
    };
    t32 = [newRating, originalRating];
    $[78] = newRating;
    $[79] = originalRating;
    $[80] = t31;
    $[81] = t32;
  } else {
    t31 = $[80];
    t32 = $[81];
  }
  useEffect(t31, t32);
  let t33;
  if ($[82] !== haveChanged || $[83] !== isValidValue) {
    t33 = () => {
      if (!haveChanged) {
        return true;
      }
      if (!isValidValue) {
        return true;
      }
      return false;
    };
    $[82] = haveChanged;
    $[83] = isValidValue;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  const getIsSaveDisabled = t33;
  const [isSaveDisabled, setIsSaveDisabled] = useState(getIsSaveDisabled());
  let t34;
  if ($[85] !== getIsSaveDisabled) {
    t34 = () => {
      setIsSaveDisabled(getIsSaveDisabled());
    };
    $[85] = getIsSaveDisabled;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  if ($[87] !== haveChanged || $[88] !== isValidValue) {
    t35 = [haveChanged, isValidValue];
    $[87] = haveChanged;
    $[88] = isValidValue;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  useEffect(t34, t35);
  let t36;
  if ($[90] !== haveChanged || $[91] !== isValidValue || $[92] !== newRating || $[93] !== requestAuth || $[94] !== setToast || $[95] !== updateCICMutation) {
    const onSave = async () => {
      const {
        success
      } = await requestAuth();
      if (!success) {
        setToast({
          message: "You must be logged in.",
          type: "error"
        });
        return;
      }
      if (!haveChanged || !isValidValue) {
        return;
      }
      await updateCICMutation.mutateAsync(newRating);
    };
    t36 = async e_0 => {
      e_0.preventDefault();
      await onSave();
    };
    $[90] = haveChanged;
    $[91] = isValidValue;
    $[92] = newRating;
    $[93] = requestAuth;
    $[94] = setToast;
    $[95] = updateCICMutation;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  const onSubmit = t36;
  const breakpoint = useBreakpoint();
  const t37 = `${isTooltip ? "" : "tw-bg-iron-950 tw-px-4 tw-py-6 lg:tw-p-8 tw-rounded-xl tw-border tw-border-solid tw-border-iron-800"} `;
  const t38 = currentCICState?.cic_ratings_left_to_give_by_rater ?? 0;
  let t39;
  if ($[97] !== isTooltip || $[98] !== minMaxValues || $[99] !== profile || $[100] !== t38) {
    t39 = <UserPageIdentityHeaderCICRateStats isTooltip={isTooltip} profile={profile} minMaxValues={minMaxValues} heroAvailableCredit={t38} />;
    $[97] = isTooltip;
    $[98] = minMaxValues;
    $[99] = profile;
    $[100] = t38;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  const t40 = `${isTooltip ? "tw-mt-4" : "tw-mt-6"}`;
  const t41 = `${isTooltip ? "" : "tw-flex-wrap"} tw-flex tw-items-end tw-gap-3`;
  const t42 = `${isTooltip ? "tw-max-w-[12rem]" : "tw-w-full sm:tw-w-auto"} tw-block tw-text-sm tw-font-normal tw-text-iron-200`;
  let t43;
  if ($[102] !== profile.query) {
    t43 = <span className="tw-whitespace-nowrap">{profile.query}:</span>;
    $[102] = profile.query;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  let t44;
  if ($[104] !== t42 || $[105] !== t43) {
    t44 = <label htmlFor="nic-rating-input" className={t42}>Your total NIC Rating of{" "}{t43}</label>;
    $[104] = t42;
    $[105] = t43;
    $[106] = t44;
  } else {
    t44 = $[106];
  }
  let t45;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <svg className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0 tw-text-iron-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <span className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-iron-800 tw-rounded-l-lg tw-border tw-border-solid tw-border-iron-800 tw-px-3">{t45}<svg className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0 tw-text-iron-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12H19" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg></span>;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  const t47 = `${isTooltip ? "tw-max-w-[12rem]" : "tw-w-full sm:tw-w-auto"} -tw-ml-0.5 tw-appearance-none tw-block tw-rounded-l-none tw-rounded-r-lg tw-border-0 tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-py-3 tw-px-3 tw-bg-iron-900 focus:tw-bg-iron-950 tw-text-iron-100 tw-font-medium tw-caret-primary-400 tw-shadow-sm hover:tw-ring-iron-600 placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 tw-text-base tw-transition tw-duration-300 tw-ease-out`;
  let t48;
  if ($[109] !== adjustStrValueToMinMax || $[110] !== adjustedRatingStr || $[111] !== t47) {
    t48 = <div className="tw-w-full tw-relative tw-flex tw-mt-1.5">{t46}<input type="text" id="nic-rating-input" value={adjustedRatingStr} onChange={onValueChange} onBlur={adjustStrValueToMinMax} required={true} autoComplete="off" className={t47} /></div>;
    $[109] = adjustStrValueToMinMax;
    $[110] = adjustedRatingStr;
    $[111] = t47;
    $[112] = t48;
  } else {
    t48 = $[112];
  }
  let t49;
  if ($[113] !== t44 || $[114] !== t48) {
    t49 = <div className="tw-w-full sm:tw-w-auto">{t44}{t48}</div>;
    $[113] = t44;
    $[114] = t48;
    $[115] = t49;
  } else {
    t49 = $[115];
  }
  const t50 = `${!isSaveDisabled ? "hover:tw-bg-primary-600 hover:tw-border-primary-600" : "tw-cursor-not-allowed tw-opacity-50"} tw-w-full sm:tw-w-auto  tw-bg-primary-500 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white 
              tw-border tw-border-solid tw-border-primary-500 tw-rounded-lg  tw-transition tw-duration-300 tw-ease-out`;
  let t51;
  if ($[116] !== mutating) {
    t51 = mutating ? <div className="tw-w-8"><CircleLoader /></div> : <>Rate</>;
    $[116] = mutating;
    $[117] = t51;
  } else {
    t51 = $[117];
  }
  let t52;
  if ($[118] !== isSaveDisabled || $[119] !== t50 || $[120] !== t51) {
    t52 = <button type="submit" disabled={isSaveDisabled} className={t50}>{t51}</button>;
    $[118] = isSaveDisabled;
    $[119] = t50;
    $[120] = t51;
    $[121] = t52;
  } else {
    t52 = $[121];
  }
  let t53;
  if ($[122] !== adjustedRatingStr || $[123] !== breakpoint || $[124] !== isTooltip || $[125] !== originalRating) {
    t53 = !isTooltip && breakpoint === "MD" && <UserRateAdjustmentHelper inLineValues={isTooltip} originalValue={originalRating} adjustedValue={getStringAsNumberOrZero(adjustedRatingStr)} adjustmentType="NIC" />;
    $[122] = adjustedRatingStr;
    $[123] = breakpoint;
    $[124] = isTooltip;
    $[125] = originalRating;
    $[126] = t53;
  } else {
    t53 = $[126];
  }
  let t54;
  if ($[127] !== t52 || $[128] !== t53) {
    t54 = <div className="tw-w-full sm:tw-w-auto"><div className="tw-w-full sm:tw-w-auto tw-inline-flex tw-items-end tw-space-x-6">{t52}{t53}</div></div>;
    $[127] = t52;
    $[128] = t53;
    $[129] = t54;
  } else {
    t54 = $[129];
  }
  let t55;
  if ($[130] !== t41 || $[131] !== t49 || $[132] !== t54) {
    t55 = <div className={t41}>{t49}{t54}</div>;
    $[130] = t41;
    $[131] = t49;
    $[132] = t54;
    $[133] = t55;
  } else {
    t55 = $[133];
  }
  let t56;
  if ($[134] !== adjustedRatingStr || $[135] !== breakpoint || $[136] !== isTooltip || $[137] !== originalRating) {
    t56 = !!(isTooltip || breakpoint !== "MD") && <UserRateAdjustmentHelper inLineValues={isTooltip} originalValue={originalRating} adjustedValue={getStringAsNumberOrZero(adjustedRatingStr)} adjustmentType="NIC" />;
    $[134] = adjustedRatingStr;
    $[135] = breakpoint;
    $[136] = isTooltip;
    $[137] = originalRating;
    $[138] = t56;
  } else {
    t56 = $[138];
  }
  let t57;
  if ($[139] !== onSubmit || $[140] !== t40 || $[141] !== t55 || $[142] !== t56) {
    t57 = <form onSubmit={onSubmit} className={t40}>{t55}{t56}</form>;
    $[139] = onSubmit;
    $[140] = t40;
    $[141] = t55;
    $[142] = t56;
    $[143] = t57;
  } else {
    t57 = $[143];
  }
  let t58;
  if ($[144] !== t37 || $[145] !== t39 || $[146] !== t57) {
    t58 = <div className={t37}>{t39}{t57}</div>;
    $[144] = t37;
    $[145] = t39;
    $[146] = t57;
    $[147] = t58;
  } else {
    t58 = $[147];
  }
  return t58;
}
function _temp2(strCIC) {
  if (strCIC.length > 1 && strCIC.startsWith("0")) {
    return strCIC.slice(1);
  }
  return strCIC;
}
function _temp(a) {
  return a.action_type === ApiProfileProxyActionType.AllocateCic;
}